<template>
  <div class="student-tooltip-settings">
    <!-- Header -->
    <div class="component-header">
      <div class="info"></div>
    </div>

    <div class="component-content">
      <cv-form class="form-group student-tooltip-body" @submit.prevent>
        <!-- Student Tooltip Name -->
        <div class="wrapper title-area">
          <dl class="field-set type-text">
            <!-- Student Tooltip -->
            <dt class="field-label">{{ t("student_tooltip_note_title_label") }}</dt>
            <dd class="field-value">
              <text-input
                ref="studentTooltipInput"
                :mandatory="true"
                :character-limit="tooltipNoteTitleCharLimit"
                :value="tooltipData.title"
                :disabled="isInputDisabled"
                :do-validate="doValidateTooltip"
                @change="handleCustomTooltipChanged"
                @validated="handleTaValidated"
              />
            </dd>
          </dl>
        </div>

        <!-- Student Tooltip notes -->
        <div class="wrapper notes">
          <dl class="field-set type-text">
            <dt class="field-label">{{ t("student_tooltip_note_content_label") }}</dt>
            <dd class="field-value">
              <text-area
                ref="tooltipNotesInput"
                :character-limit="tooltipNoteContentCharLimit"
                :placeholder="t('student_tooltip_notes_input_placeholder')"
                :value="tooltipData.content"
                :disabled="isInputDisabled"
                @change="handleTooltipNotesContentChanged"
              />
            </dd>
          </dl>
        </div>
      </cv-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import { ref, computed, watch, onMounted, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits(["isDirty", "validated", "saveData"]);

// Props
const props = defineProps({
  task: { type: String, default: "" },
  selectedTooltip: { type: Object, default: () => ({}) },
});

// Refs
const tooltipNoteTitleCharLimit = ref(50);
const tooltipNoteContentCharLimit = ref(500);
const dirty = ref(false);
const tooltipData = ref({ ...props.selectedTooltip });
const doValidateTooltip = ref(false);

const studentTooltipInput = useTemplateRef("studentTooltipInput");
const tooltipNotesInput = useTemplateRef("tooltipNotesInput");

const { t } = useI18n();

// Computed
const isInputDisabled = computed(() => props.task !== "");

// Methods
const isCreate = () => !props.selectedTooltip || Object.keys(props.selectedTooltip).length === 0;

const setDirty = () => {
  dirty.value = true;
};

const handleCustomTooltipChanged = (newValue: string) => {
  setDirty();
  tooltipData.value.title = newValue;
};

const handleTooltipNotesContentChanged = (newValue: string) => {
  setDirty();
  tooltipData.value.content = newValue;
};

const handleTaValidated = () => {
  doValidateTooltip.value = false;
};

const validate = () => validateNoteTitle() && validateNoteContent();

const validateNoteTitle = () => {
  const tooltipNoteTitle = tooltipData.value.title || "";

  if (isCreate() && !dirty.value) {
    doValidateTooltip.value = true;
  }

  if (dirty.value) {
    doValidateTooltip.value = true;
  }

  return (
    tooltipNoteTitle.trim().length > 0 && tooltipNoteTitle.length <= tooltipNoteTitleCharLimit.value
  );
};

const validateNoteContent = () => {
  return tooltipNotesInput.value?.validateMaxLimit();
};

const formData = () => ({
  title: tooltipData.value.title,
  content: tooltipData.value.content || "",
});

const focusTooltip = () => {
  const input = DOMUtils.elOf(studentTooltipInput.value.$el, "input")[0];
  input?.focus();
};

// Watch
watch(
  () => props.task,
  (task) => {
    if (task === "checkDirty") {
      emit("isDirty", dirty.value);
    } else if (task === "validate") {
      emit("validated", { code: validate() ? "ok" : "error" });
    } else if (task === "save") {
      emit("saveData", { code: "ok", data: formData() });
    }
  },
);

// Mounted
onMounted(() => {
  focusTooltip();
});
</script>
